/**
 * Every route on the guest surface, and the screen that owns it.
 *
 * ## The QR URL shape is a PROPOSAL
 *
 * The frozen contract defines the *fields* a scanned QR must yield —
 * `table_qr_token`, `tenant_id`, `table_id` on `OpenGuestSessionRequest` (D-019)
 * — but **nothing anywhere specifies the URL those fields are printed in**. The
 * two entry routes below are therefore a proposal, chosen so the mapping to the
 * request body is one to one and nothing has to be parsed or guessed:
 *
 *     /s?t=<table_qr_token>&m=<tenant_id>&b=<table_id>     dine-in, Q1
 *     /q?t=<table_qr_token>&m=<tenant_id>                  queue-ahead, Q12
 *
 * Query parameters rather than path segments because a token is opaque and may
 * contain anything URL-safe, and because adding a field later (a printed menu
 * version, a language hint) does not change the shape of what is already
 * laminated onto a table.
 *
 * **This must be agreed with whoever generates the printed card before a single
 * QR is printed.** `POST /v1/location/tables/{table_id}/qr` mints the token;
 * what URL it is rendered into is undecided, and rotation always requires a
 * reprint, so a wrong guess here is a physical recall.
 *
 * ## Two entries that must not be confused
 *
 * Q1 (`/s`) has a table identity; Q12 (`/q`) has none and is a different
 * product moment (03 §Q12 says so explicitly). They are separate routes rather
 * than one route with a mode flag for exactly that reason.
 */

export const ROUTES = {
  /** Q1 · scan landing. Dine-in table QR. */
  scan: '/s',
  /** Q12 · queue-ahead landing. Counter or poster QR — no table identity. */
  queue: '/q',
  /** Q2 · language toggle. Reachable from every screen without losing the cart. */
  language: '/language',
  /** Q3 · menu browse. */
  menu: '/menu',
  /** Q4 · item detail and modifiers. */
  item: '/menu/:itemId',
  /** Q5 · cart. */
  cart: '/cart',
  /** Q7 · order-first submit (full-service default). */
  review: '/review',
  /** Q8 · pay-first checkout. Hands the guest to ReservePay's hosted page. */
  pay: '/pay/:orderId',
  /**
   * Q9 · payment verification, after ReservePay hands the guest back.
   *
   * **ReservePay does not send anybody here.** `POKPOK_GUEST_RETURN_URL` is
   * `https://<guest host>/status` — Q14's route — set in `pokpok-infra` and not
   * negotiable from this repository. This route exists so the return leg is one
   * screen that can be deep-linked, tested and pointed at, and Q14 carries the
   * notice that reaches it. If the return URL is ever repointed, this is the
   * path to point it at.
   */
  paymentReturn: '/pay/:orderId/return',
  /** Q14 · order status, dine-in. */
  status: '/status',
  /**
   * Feedback · a guest leaves a post-bill star rating and an optional comment.
   *
   * Runs inside the guest's own session — the same table QR credential that
   * ordered the meal — because `POST /v1/public/feedback` authenticates with the
   * `guestSessionToken` and resolves the tenant and location from it. Nothing is
   * read off `?m=` into the request body: the contract takes the tenant from the
   * token, never the client (see the screen and `ApiClient.submitPublicFeedback`).
   */
  feedback: '/feedback',
  /** Q17 · order confirmation and receipt. Deep-linkable, survives a refresh. */
  receipt: '/order/:orderId',
  /** Q19 · expired / regenerated / invalid QR. Also the no-QR landing. */
  invalid: '/invalid',
  /** Q20 · venue closed / not accepting orders. */
  closed: '/closed',

  /**
   * ## Customer web booking (BookWeb → BookDone → BookManage)
   *
   * A separate entry from the QR flow, and a separate credential model. These
   * are reached from a **booking link** the venue publishes — not a scanned
   * table QR — so they carry only `tenant_id` (the `m` parameter, exactly as the
   * QR routes do) and never open a guest session. The `manage_token` minted by
   * the create call, not a session token, is what carries the guest into
   * BookDone and BookManage; it rides as a path segment so a booking link is a
   * real, shareable, deep-linkable URL.
   */
  /** BookWeb · the public booking form. `/book?m=<tenant_id>`. */
  book: '/book',
  /** BookDone · confirmation, right after creating. `/book/done/:token?m=<tenant_id>`. */
  bookDone: '/book/done/:token',
  /** BookManage · view + cancel a booking. `/book/manage/:token?m=<tenant_id>`. */
  bookManage: '/book/manage/:token',
} as const;

export type RouteName = keyof typeof ROUTES;

/** The literal path patterns, so a typo cannot become a route nothing serves. */
export type RoutePattern = (typeof ROUTES)[RouteName];

/** Query parameter names on the two entry routes. Printed into the QR. */
export const QR_PARAMS = {
  /** `table_qr_token` — the opaque token. */
  token: 't',
  /** `tenant_id` — printed beside the token. Required in practice (D-019). */
  tenant: 'm',
  /** `table_id` — printed beside the token. Narrows the lookup to one row. */
  table: 'b',
} as const;

/** Fill `:params` in a route pattern. Unknown extras become query parameters. */
export function href(
  route: (typeof ROUTES)[RouteName],
  params: Record<string, string> = {},
  query: Record<string, string | undefined> = {},
): string {
  const remaining = {...params};
  const path = route.replace(/:(\w+)/g, (whole, key: string) => {
    const value = remaining[key];
    if (value === undefined) return whole;
    delete remaining[key];
    return encodeURIComponent(value);
  });
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value !== undefined) search.set(key, value);
  }
  const queryString = search.toString();
  return queryString ? `${path}?${queryString}` : path;
}
